// frontend/src/pages/EventsPage.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Pagination,
  Skeleton,
  Alert,
  IconButton,
  Button,
  useTheme,
  alpha,
  Paper,
  Tooltip,
  Drawer
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
  Event as EventIcon,
  Add as AddIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { eventsAPI } from '../services/eventsAPI';
import { authAPI } from '../services/api';
import { getCached, requestCacheKey } from '../services/requestCache';
import EventCard from '../components/Events/EventCard';
import EventCalendarModal from '../components/Events/EventCalendarModal';

// ============================================
// КОНСТАНТЫ
// ============================================

const EVENT_TYPES = [
  { value: '', label: 'Все типы', icon: '📋' },
  { value: 'conference', label: 'Конференция', icon: '🗣️', color: '#4361ee' },
  { value: 'seminar', label: 'Семинар', icon: '📚', color: '#3a0ca3' },
  { value: 'symposium', label: 'Симпозиум', icon: '🔬', color: '#7209b7' },
  { value: 'workshop', label: 'Воркшоп', icon: '🛠️', color: '#f72585' },
  { value: 'school', label: 'Школа', icon: '🏫', color: '#4cc9f0' },
  { value: 'congress', label: 'Конгресс', icon: '🌍', color: '#4895ef' },
  { value: 'forum', label: 'Форум', icon: '🗣️', color: '#560bad' },
  { value: 'roundtable', label: 'Круглый стол', icon: '🔄', color: '#b5179e' },
  { value: 'competition', label: 'Конкурс', icon: '🏆', color: '#f8961e' },
  { value: 'festival', label: 'Фестиваль', icon: '🎪', color: '#f94144' }
];

const EVENT_LEVELS = [
  { value: '', label: 'Все уровни' },
  { value: 'international', label: 'Международный' },
  { value: 'national', label: 'Всероссийский' },
  { value: 'interregional', label: 'Межрегиональный' },
  { value: 'regional', label: 'Региональный' },
  { value: 'university', label: 'Внутривузовский' },
  { value: 'interuniversity', label: 'Межвузовский', color: '#FF6B6B' }
];

const EVENT_STATUSES = [
  { value: '', label: 'Все статусы' },
  { value: 'published', label: 'Опубликовано' },
  { value: 'in_progress', label: 'Идёт' },
  { value: 'completed', label: 'Завершено' },
  { value: 'draft', label: 'Черновик' }
];

// ============================================
// СТИЛИЗОВАННЫЕ КОМПОНЕНТЫ
// ============================================

const PageContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  paddingTop: 0,
  paddingBottom: theme.spacing(6),
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center'
}));

// 🔥 ШАПКА В СТИЛЕ DASHBOARD
const AnimatedHeader = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: '960px',
  minWidth: '640px',
  minHeight: 300,
  marginTop: '-100px',
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(3),
  background: 'linear-gradient(135deg, #284b63 0%, #3c6e71 50%, #58b6b9 100%)',
  animation: 'AnimateBG 10s ease infinite',
  backgroundSize: '300% 300%',
  overflow: 'hidden',
  boxShadow: '0 8px 16px -4px rgba(0, 0, 0, 0.2)',
  '@keyframes AnimateBG': {
    '0%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0% 50%' }
  },
  [theme.breakpoints.down('md')]: {
    width: 'calc(100% - 32px)',
    minWidth: 'auto',
    marginLeft: '16px',
    marginRight: '16px'
  }
}));

// 🔥 КОНТЕЙНЕР ДЛЯ КУБИКОВ
const CubesContainer = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  zIndex: 1
});

// 🔥 СТИЛИ ДЛЯ КУБИКОВ
const Cube = styled(Box)(({ delay, size, left }) => ({
  position: 'absolute',
  display: 'block',
  listStyle: 'none',
  width: size || '20px',
  height: size || '20px',
  background: 'rgba(255, 255, 255, 0.15)',
  bottom: '-150px',
  left: left || 'auto',
  animation: 'animate 25s ease-in infinite',
  animationDelay: delay || '0s',
  '@keyframes animate': {
    '0%': {
      transform: 'translateY(0) rotate(0deg)',
      opacity: 1,
      borderRadius: 0
    },
    '100%': {
      transform: 'translateY(-1000px) rotate(720deg)',
      opacity: 0,
      borderRadius: '50%'
    }
  }
}));

// 🔥 КОНТЕЙНЕР ДЛЯ КОНТЕНТА ШАПКИ - прижимаем к низу
const HeaderContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginTop:110,
  zIndex: 2,
  padding: theme.spacing(2,5),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  color: theme.palette.common.white
}));

// 🔥 ЛЕВАЯ ЧАСТЬ С ТЕКСТОМ И КНОПКОЙ
const HeaderLeft = styled(Box)({
  display: 'flex',
  width: '100%',
  justifyContent: 'flex-start',
  flexDirection: 'column',
  gap: 1
});

// 🔥 ПРАВАЯ ЧАСТЬ СО СТАТИСТИКОЙ
const HeaderRight = styled(Box)({
  display: 'flex',
  gap: 2,
  width: '100%',
  marginLeft: 'auto',
});

// 🔥 КНОПКА СОЗДАНИЯ МЕРОПРИЯТИЯ (как на Dashboard)
const CreateEventButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(3),
  left: theme.spacing(5),
  color: 'white',
  backgroundColor: 'rgba(255, 255, 255, 0.15)',
  border: '0.5px solid rgba(255, 255, 255, 0.8)',
  zIndex: 10,
  padding: theme.spacing(1),
  backdropFilter: 'blur(4px)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    borderColor: 'white'
  }
}));

const StatChip = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  borderRadius: theme.spacing(3),
  backgroundColor: 'rgba(255,255,255,0.15)',
  backdropFilter: 'blur(4px)',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1)
}));

// 🔥 СТИЛИЗОВАННЫЙ БЛОК ФИЛЬТРОВ
const FilterSection = styled(Paper)(({ theme }) => ({
  width: '100%',
  maxWidth: 960,
  padding: theme.spacing(2, 3),
  marginBottom: theme.spacing(4),
  borderRadius: theme.spacing(4),
  backgroundColor: theme.palette.background.paper,
  boxShadow: 'none'
}));

const EventsGrid = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 960,
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
  gap: theme.spacing(3),
  marginBottom: theme.spacing(4)
}));


// ============================================
// SKELETON КАРТОЧКИ МЕРОПРИЯТИЯ
// ============================================

const EventCardSkeleton = ({ theme }) => (
  <Paper
    elevation={0}
    sx={{
      overflow: 'hidden',
      borderRadius: 4,
      background: 'rgba(255,255,255,0.72)',
      backdropFilter: 'blur(22px)',
      WebkitBackdropFilter: 'blur(22px)',
      border: '1px solid rgba(255,255,255,0.78)',
      boxShadow: '0 10px 30px rgba(25, 40, 45, 0.07)',
      minWidth: 0
    }}
  >
    <Box sx={{ position: 'relative' }}>
      <Skeleton
        variant="rectangular"
        animation="wave"
        sx={{
          height: 190,
          transform: 'none',
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          '&::after': {
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.52), transparent)'
          }
        }}
      />
      <Box sx={{
        position: 'absolute',
        top: 12,
        left: 12,
        right: 12,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
      }}>
        <Skeleton variant="rounded" animation="wave" width={82} height={28}
          sx={{ borderRadius: 3, bgcolor: 'rgba(255,255,255,0.55)' }} />
        <Skeleton variant="rounded" animation="wave" width={74} height={28}
          sx={{ borderRadius: 3, bgcolor: 'rgba(255,255,255,0.55)' }} />
      </Box>
    </Box>

    <Box sx={{ p: 2.2 }}>
      <Skeleton animation="wave" variant="text" width="82%" height={30} />
      <Skeleton animation="wave" variant="text" width="100%" height={20} />
      <Skeleton animation="wave" variant="text" width="68%" height={20} sx={{ mb: 1.5 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.2 }}>
        <Skeleton animation="wave" variant="circular" width={22} height={22} />
        <Skeleton animation="wave" variant="text" width="48%" height={20} />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Skeleton animation="wave" variant="rounded" width={105} height={28} sx={{ borderRadius: 3 }} />
        <Skeleton animation="wave" variant="text" width={72} height={20} />
      </Box>
    </Box>
  </Paper>
);

const EventCardsSkeleton = ({ theme, count = 6 }) => (
  <>
    {Array.from({ length: count }).map((_, index) => (
      <EventCardSkeleton key={`event-skeleton-${index}`} theme={theme} />
    ))}
  </>
);

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(4)
}));

// ============================================
// ОСНОВНОЙ КОМПОНЕНТ
// ============================================

const EventsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const savedState = (() => { try { return JSON.parse(sessionStorage.getItem('eventsPageState') || 'null') || {}; } catch (_) { return {}; } })();
  const initialFilters = savedState.filters || { type: '', level: '', status: '', search: '', branch: '' };
  const initialPage = Number(savedState.page) > 0 ? Number(savedState.page) : 1;
  const initialParams = { page: initialPage, page_size: 15, ...initialFilters };
  const initialCached = getCached(requestCacheKey('events-list', initialParams));
  const [events, setEvents] = useState(initialCached?.results || []);
  const [loading, setLoading] = useState(!initialCached);
  const [error, setError] = useState('');
  const [page, setPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(initialCached ? Math.max(1, Math.ceil((initialCached.count || 0) / 15)) : 1);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [branches, setBranches] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    authAPI.getBranches().then((data) => {
      const list = Array.isArray(data) ? data : [];
      setBranches(list);
      // Миграция старого сохранённого состояния: ранее фильтр мог хранить ID,
      // а API мероприятий ожидает название филиала в organizer_branches.
      setFilters((current) => {
        const matched = list.find((branch) => String(branch.id) === String(current.branch));
        return matched ? { ...current, branch: matched.name } : current;
      });
    }).catch(err => console.error('Не удалось загрузить филиалы:', err));
  }, []);

  // 🔥 РЕАЛЬНАЯ СТАТИСТИКА
  const [stats, setStats] = useState({
    total: 0,
    published: 0,
    inProgress: 0,
    completed: 0
  });

  // Фильтры
  const [filters, setFilters] = useState(initialFilters);

  // Данные для кубиков
  const cubes = [
    { left: '5%', size: '60px', delay: '0s' },
    { left: '15%', size: '25px', delay: '2s' },
    { left: '25%', size: '40px', delay: '4s' },
    { left: '35%', size: '80px', delay: '1s' },
    { left: '45%', size: '20px', delay: '3s' },
    { left: '55%', size: '70px', delay: '5s' },
    { left: '65%', size: '35px', delay: '2s' },
    { left: '75%', size: '50px', delay: '6s' },
    { left: '85%', size: '30px', delay: '4s' },
    { left: '95%', size: '45px', delay: '0s' },
  ];

  // Загрузка мероприятий и статистики
  useEffect(() => {
    fetchEvents();
    fetchStats();
  }, [page, filters]);

  const fetchEvents = async () => {
    const params = { page, page_size: 15, ...filters };
    const cached = getCached(requestCacheKey('events-list', params));
    if (!cached && events.length === 0) setLoading(true);
    try {
      const response = await eventsAPI.getEvents(params);
      setEvents(response.results || []);
      setTotalPages(Math.ceil((response.count || 0) / 15));
    } catch (err) {
      console.error('❌ Ошибка загрузки мероприятий:', err);
      setError('Не удалось загрузить мероприятия');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => () => {
    try { sessionStorage.setItem('eventsPageState', JSON.stringify({ page, filters, scrollY: window.scrollY })); } catch (_) {}
  }, [page, filters]);

  // Сохраняем состояние списка при переходе в карточку мероприятия.
  useEffect(() => {
    try { sessionStorage.setItem('eventsPageState', JSON.stringify({ page, filters, scrollY: window.scrollY })); } catch (_) {}
  }, [page, filters]);
  useEffect(() => {
    if (!loading) {
      try { const saved = JSON.parse(sessionStorage.getItem('eventsPageState') || 'null'); if (saved && Number.isFinite(saved.scrollY)) requestAnimationFrame(() => window.scrollTo(0, saved.scrollY)); } catch (_) {}
    }
  }, [loading]);

  // 🔥 ФУНКЦИЯ ДЛЯ ЗАГРУЗКИ РЕАЛЬНОЙ СТАТИСТИКИ
  const fetchStats = async () => {
    try {
      // Получаем все мероприятия без пагинации для подсчета статистики
      const allEvents = await eventsAPI.getEvents({ page_size: 1000 });
      const eventsList = allEvents.results || [];

      const total = eventsList.length;
      const published = eventsList.filter(e => e.status === 'published').length;
      const inProgress = eventsList.filter(e => e.status === 'in_progress').length;
      const completed = eventsList.filter(e => e.status === 'completed').length;

      setStats({
        total,
        published,
        inProgress,
        completed
      });
    } catch (err) {
      console.error('❌ Ошибка загрузки статистики:', err);
    }
  };

  // 🔥 ОБРАБОТЧИК СОЗДАНИЯ МЕРОПРИЯТИЯ
  const handleCreateEvent = () => {
    navigate('/events/create');
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({ type: '', level: '', status: '', search: '', branch: '' });
    setPage(1);
  };

  const handleEventClick = (eventId) => {
    navigate(`/events/${eventId}`);
  };

  const getTypeInfo = (type) => {
    return EVENT_TYPES.find(t => t.value === type) || EVENT_TYPES[0];
  };

  const getLevelInfo = (level) => {
  const found = EVENT_LEVELS.find(l => l.value === level);
  if (found) return found;
  // Если уровень не найден, возвращаем серый цвет
  return {
    value: '',
    label: 'Неизвестно',
    color: '#757575'
  };
};

  const hasActiveFilters = filters.type || filters.level || filters.status || filters.search || filters.branch;

  return (
    <PageContainer>
      {/* 🔥 АНИМИРОВАННАЯ ШАПКА */}
      <AnimatedHeader>
        <CubesContainer>
          {cubes.map((cube, index) => (
            <Cube
              key={index}
              left={cube.left}
              size={cube.size}
              delay={cube.delay}
            />
          ))}
        </CubesContainer>

        {/* 🔥 КНОПКА СОЗДАНИЯ МЕРОПРИЯТИЯ - как на Dashboard */}
        <Tooltip title="Создать мероприятие">
          <CreateEventButton onClick={handleCreateEvent} size="small">
            <AddIcon sx={{ fontSize: 24 }} />
          </CreateEventButton>
        </Tooltip>

        <HeaderContent>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            height: '100%',
            width: '100%'
          }}>
            {/* 1 строка - Заголовок (слева) */}
            <Box sx={{ width: '100%' }}>
              <Typography
                variant="h3"
                fontWeight="900"
                sx={{
                  fontFamily: '"Montserrat", sans-serif',
                  letterSpacing: '-0.5px',
                  opacity: '0.9',
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  textAlign: 'left'
                }}
              >
                МЕРОПРИЯТИЯ
              </Typography>
            </Box>

            {/* 2 строка - Описание (слева) */}
            <Box sx={{ width: '100%', mt: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: '"Noto Sans", sans-serif',
                  fontWeight: 100,
                  fontSize: 18,
                  opacity: 0.7,
                  textAlign: 'left'
                }}
              >
                Научные конференции, семинары и другие события университета
              </Typography>
            </Box>

            {/* 3 строка - Чипсы (справа) */}
            <Box sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              mt: 2
            }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <StatChip
                  elevation={0}
                  onClick={() => setCalendarOpen(true)}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(255,255,255,0.25)' } }}
                >
                  <EventIcon fontSize="small" />
                  <Typography variant="body2">Всего: {stats.total}</Typography>
                </StatChip>
                <StatChip elevation={0}>
                  <Typography variant="body2">Активных: {stats.published + stats.inProgress}</Typography>
                </StatChip>
                <StatChip elevation={0}>
                  <Typography variant="body2">Завершено: {stats.completed}</Typography>
                </StatChip>
              </Box>
            </Box>
          </Box>
        </HeaderContent>
      </AnimatedHeader>

      {/* 🔥 СТИЛИЗОВАННЫЙ БЛОК ФИЛЬТРОВ */}
      <FilterSection elevation={0} sx={{ background: (theme) => alpha(theme.palette.background.paper, 0.72), backdropFilter: 'blur(22px)', WebkitBackdropFilter: 'blur(22px)', border: '1px solid', borderColor: 'rgba(255,255,255,0.42)', boxShadow: '0 14px 40px rgba(15,23,42,0.055)' }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            fullWidth
            sx={{ flex: '1 1 280px', '& .MuiOutlinedInput-root': { borderRadius: '18px', backgroundColor: alpha(theme.palette.primary.main, 0.025) } }}
            placeholder="Поиск мероприятий..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            size="small"
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'primary.main' }} /></InputAdornment>,
              endAdornment: filters.search ? <InputAdornment position="end"><IconButton size="small" onClick={() => handleFilterChange('search', '')}><ClearIcon /></IconButton></InputAdornment> : null
            }}
          />
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setFiltersOpen(true)}
            sx={{ minHeight: 42, px: 2.2, borderRadius: '16px', textTransform: 'none', fontWeight: 600, borderColor: alpha(theme.palette.primary.main, 0.28), background: alpha(theme.palette.background.paper, 0.55), backdropFilter: 'blur(12px)', '&:hover': { background: alpha(theme.palette.primary.main, 0.07), borderColor: 'primary.main' } }}
          >
            Фильтры{hasActiveFilters ? ` (${[filters.type, filters.level, filters.status, filters.branch].filter(Boolean).length})` : ''}
          </Button>
          {hasActiveFilters && <Button onClick={handleClearFilters} color="inherit" sx={{ borderRadius: '14px', textTransform: 'none' }}>Сбросить</Button>}
        </Box>
      </FilterSection>

      <Drawer anchor="right" open={filtersOpen} onClose={() => setFiltersOpen(false)} PaperProps={{ sx: {
    width: { xs: 'min(100vw, 420px)', sm: 420 },
    maxWidth: '100vw',
    boxSizing: 'border-box',
    p: 0,
    overflow: 'hidden',
    borderRadius: { xs: '24px 0 0 24px', sm: '28px 0 0 28px' },
    border: '1px solid rgba(255,255,255,0.62)',
    background: (theme) => theme.palette.mode === 'dark'
      ? 'linear-gradient(145deg, rgba(28,32,38,0.94), rgba(22,25,31,0.88))'
      : 'linear-gradient(145deg, rgba(255,255,255,0.94), rgba(245,248,250,0.88))',
    backdropFilter: 'blur(28px) saturate(180%)',
    WebkitBackdropFilter: 'blur(28px) saturate(180%)',
    boxShadow: '-24px 0 70px rgba(15,23,42,0.20)',
    display: 'flex',
    flexDirection: 'column'
  } }}>
        <Box sx={{
      px: 3, py: 2.5,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      borderBottom: '1px solid', borderColor: 'divider',
      background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.035)' : 'rgba(255,255,255,0.48)'
    }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>Фильтры мероприятий</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>Уточните параметры поиска</Typography>
          </Box>
          <IconButton onClick={() => setFiltersOpen(false)} aria-label="Закрыть фильтры" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '14px' }}><CloseIcon /></IconButton>
        </Box>
        <Box sx={{ p: 3, overflowY: 'auto', flex: 1 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Тип мероприятия</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2.2, '& .MuiOutlinedInput-root': { borderRadius: '16px', backgroundColor: 'rgba(255,255,255,0.28)' }, '& .MuiInputLabel-root': { fontWeight: 500 } }}>
            <InputLabel>Тип</InputLabel>
            <Select value={filters.type} label="Тип" onChange={(e) => handleFilterChange('type', e.target.value)}>
              {EVENT_TYPES.map((item) => <MenuItem key={item.value} value={item.value}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><span>{item.icon}</span><span>{item.label}</span></Box></MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Уровень</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2.2, '& .MuiOutlinedInput-root': { borderRadius: '16px', backgroundColor: 'rgba(255,255,255,0.28)' }, '& .MuiInputLabel-root': { fontWeight: 500 } }}>
            <InputLabel>Уровень</InputLabel>
            <Select value={filters.level} label="Уровень" onChange={(e) => handleFilterChange('level', e.target.value)}>
              {EVENT_LEVELS.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Статус</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2.2, '& .MuiOutlinedInput-root': { borderRadius: '16px', backgroundColor: 'rgba(255,255,255,0.28)' }, '& .MuiInputLabel-root': { fontWeight: 500 } }}>
            <InputLabel>Статус</InputLabel>
            <Select value={filters.status} label="Статус" onChange={(e) => handleFilterChange('status', e.target.value)}>
              {EVENT_STATUSES.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Филиал</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2.2, '& .MuiOutlinedInput-root': { borderRadius: '16px', backgroundColor: 'rgba(255,255,255,0.28)' }, '& .MuiInputLabel-root': { fontWeight: 500 } }}>
            <InputLabel>Филиал</InputLabel>
            <Select value={filters.branch || ''} label="Филиал" onChange={(e) => handleFilterChange('branch', e.target.value)}>
              <MenuItem value="">Все филиалы</MenuItem>
              {branches.map((branch) => <MenuItem key={branch.id} value={branch.name}>{branch.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ p: 2.5, mt: 'auto', display: 'flex', gap: 1.5, borderTop: '1px solid', borderColor: 'divider', background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.035)' : 'rgba(255,255,255,0.52)' }}>
          <Button fullWidth variant="outlined" onClick={handleClearFilters} sx={{ borderRadius: '15px', py: 1.2, textTransform: 'none' }}>Сбросить</Button>
          <Button fullWidth variant="contained" onClick={() => setFiltersOpen(false)} sx={{ borderRadius: '15px', py: 1.2, textTransform: 'none', boxShadow: 'none' }}>Готово</Button>
        </Box>
      </Drawer>

      {/* Сетка мероприятий */}
      {loading && events.length === 0 ? (
        <EventsGrid>
          <EventCardsSkeleton theme={theme} count={6} />
        </EventsGrid>
      ) : error ? (
        <Alert severity="error" sx={{ maxWidth: 960, width: '100%', borderRadius: 3 }}>
          {error}
        </Alert>
      ) : events.length === 0 ? (
        <Box sx={{
          maxWidth: 960,
          width: '100%',
          p: 8,
          textAlign: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          borderRadius: 3
        }}>
          <EventIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Мероприятия не найдены
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Попробуйте изменить параметры поиска
          </Typography>
        </Box>
      ) : (
        <EventsGrid>
          {events.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              typeInfo={getTypeInfo(event.type)}
              levelInfo={getLevelInfo(event.level)}
              onClick={() => handleEventClick(event.id)}
            />
          ))}
        </EventsGrid>
      )}

      {/* Пагинация */}
      {totalPages > 1 && (
        <PaginationContainer>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
            sx={{
              '& .MuiPaginationItem-root': {
                borderRadius: 2
              }
            }}
          />
        </PaginationContainer>
      )}
            <EventCalendarModal
            open={calendarOpen}
            onClose={() => setCalendarOpen(false)}
          />
    </PageContainer>
  );
};

export default EventsPage;